import { AvatarDropdown, AvatarName, Footer, Question, SelectLang, WorkspaceSwitcher } from '@/components';
import { loadStudioNav, type StudioNavItem } from '@/pages/Gosh/remote';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { LinkOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import * as AntdIcons from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link, useModel } from '@umijs/max';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { Switch, Space, Tag, Typography, Select, ConfigProvider } from 'antd';
import Clock from '@/components/Clock';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { theme as antdTheme } from 'antd';

// 启用 dayjs 插件
dayjs.extend(utc);
dayjs.extend(timezone);

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * ProLayout 主题配置枚举
 */
const proLayoutThemeConfigs = {
  light: {
    token: {
      bgLayout: '#faf9f7',           // 主布局背景 - 纸质米色
      header: {
        colorBgHeader: '#ffffff',   // 头部背景 - 纯白，比主背景亮
        heightLayoutHeader: 48,
      },
      sider: {
        colorMenuBackground: '#ffffff', // 侧边栏背景 - 纯白，与头部一致
      },
      pageContainer: {
        colorBgPageContainer: '#faf9f7', // 页面容器背景 - 纸质米色
        paddingBlockPageContainerContent: 16,
        paddingInlinePageContainerContent: 16,
      },
    },

    navTheme: 'light' as const,
  },
  dark: {
    token: {
      bgLayout: '#000000',
      header: {
        colorBgHeader: '#141414',
        heightLayoutHeader: 48,
      },
      sider: {
        colorMenuBackground: '#141414',
      },
      pageContainer: {
        colorBgPageContainer: '#000000',
        paddingBlockPageContainerContent: 16,
        paddingInlinePageContainerContent: 16,
      },
    },
    navTheme: 'realDark' as const,
  },
};

/**
 * Ant Design 主题配置枚举
 */
const themeConfigs = {
  light: {
    algorithm: [antdTheme.defaultAlgorithm, antdTheme.compactAlgorithm],
    token: {
      'root-entry-name': 'variable',
      colorPrimary: "#00CED1",
      // borderRadius: 6,
      // fontSize: 14,
      // sizeStep: 3,
      // sizeUnit: 3,
      // Light 模式背景色配置
      colorBgBase: '#ffffff',        // 基础背景色
      colorBgContainer: '#ffffff',    // 容器背景色
      colorBgElevated: '#ffffff',     // 悬浮背景色
      colorBgLayout: '#ffffff',       // 布局背景色
      colorBgMask: 'rgba(0, 0, 0, 0.45)', // 遮罩背景色
      // colorBgSpotlight: 'rgba(0,0,0,0.85)',    // 聚光灯背景色
      colorBgTextHover: 'rgba(0, 0, 0, 0.06)', // 文本悬停背景色
      colorBgTextActive: 'rgba(0, 0, 0, 0.15)', // 文本激活背景色
      // Light 模式文本颜色
      colorTextBase: '#000000',       // 基础文本颜色
      colorText: '#000000',           // 文本颜色
      colorTextSecondary: '#666666', // 次要文本颜色
      colorTextTertiary: '#999999',  // 第三级文本颜色
      colorTextQuaternary: '#cccccc', // 第四级文本颜色
    },
    components: {
      Table: {
        headerBg: '#fafafa',
      },
      // Segmented 默认 trackBg = colorBgLayout；本站 colorBgLayout 是白，会和选中白块叠死
      Segmented: {
        trackBg: '#f5f5f5',
        itemSelectedBg: '#ffffff',
      },
      Menu: {
        itemHeight: 32
      }
    },
  },
  dark: {
    algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm],
    token: {
      'root-entry-name': 'variable',
      colorPrimary: "#00CED1",
      // borderRadius: 6,
      // fontSize: 14,
      // sizeStep: 3,
      // sizeUnit: 3,
      // Dark 模式背景色配置
      colorBgBase: '#000000',        // 基础背景色
      colorBgContainer: '#141414',    // 容器背景色
      colorBgElevated: '#1f1f1f',     // 悬浮背景色
      colorBgLayout: '#000000',       // 布局背景色
      colorBgMask: 'rgba(0, 0, 0, 0.65)', // 遮罩背景色
      // colorBgSpotlight: '#1f1f1f',    // 聚光灯背景色
      colorBgTextHover: 'rgba(255, 255, 255, 0.06)', // 文本悬停背景色
      colorBgTextActive: 'rgba(255, 255, 255, 0.15)', // 文本激活背景色
      // Dark 模式文本颜色
      colorTextBase: '#ffffff',       // 基础文本颜色
      colorText: '#ffffff',           // 文本颜色
      colorTextSecondary: '#a6a6a6', // 次要文本颜色
      colorTextTertiary: '#737373',  // 第三级文本颜色
      colorTextQuaternary: '#404040', // 第四级文本颜色
    },
    components: {
      Table: {
        headerBg: '#141414',
      },
      // 暗色：轨道用容器色，选中用 elevated，避免和纯黑布局叠死
      Segmented: {
        trackBg: '#141414',
        itemSelectedBg: '#1f1f1f',
      },
      Menu: {
        itemHeight: 32
      }
    },
  },
};

/**
 * 获取动态主题配置
 * @param theme 主题类型 'light' | 'dark'
 */
const getDynamicThemeConfig = (theme: 'light' | 'dark') => {
  return themeConfigs[theme];
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  currentWorkspace?: { id: number; name: string; slug: string; type: string } | null;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  theme?: 'light' | 'dark';
}> {
  const fetchUserInfo = async () => {
    try {
      const data = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return data;
    } catch (error) {
      localStorage.removeItem('token');
      const { pathname, search, hash } = history.location;
      if (pathname !== loginPath) {
        const redirect = encodeURIComponent(pathname + search + hash);
        history.push(`${loginPath}?redirect=${redirect}`);
      }
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
      theme: 'light',
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
    theme: 'light',
  };
}


type MenuNode = {
  path?: string;
  name?: string;
  icon?: React.ReactNode;
  children?: MenuNode[];
};

function studioMenuIcon(name: string) {
  const icons = AntdIcons as unknown as Record<string, React.ComponentType | undefined>;
  const Icon = icons[name];
  if (!Icon) return undefined;
  return <Icon />;
}

function withStudioMenu(menuData: MenuNode[], items: StudioNavItem[]) {
  return menuData.map((item) => {
    if (item.path !== '/gosh') return item;
    const children = (item.children ?? []).map((child) => {
      if (child.path !== '/gosh/studio') return child;
      return {
        ...child,
        children: items.map((nav) => ({
          path: `/gosh/studio/${nav.path}`,
          name: nav.name,
          icon: studioMenuIcon(nav.icon),
        })),
      };
    });
    return { ...item, children };
  });
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  const { theme, toggleTheme } = useModel('global');
  const [studioNav, setStudioNav] = useState<StudioNavItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadStudioNav()
      .then((items) => {
        if (!cancelled) setStudioNav(items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    // actionsRender: () => [<Question key="doc" />, <SelectLang key="SelectLang" />],
    actionsRender: () => [
      <WorkspaceSwitcher key="workspace" />,
      <Clock
        key="clock"
        timezones={[
          { timezone: 'Asia/Tokyo', flag: '🇯🇵', label: '日本' },
          { timezone: 'Asia/Shanghai', flag: '🇨🇳', label: '中国' },
        ]}
        format="YYYY-MM-DD dddd HH:mm:ss"
        style={{
          marginRight: '16px',
        }}
      />,
      <Switch
        key="theme-switch"
        checked={theme === 'dark'}
        onChange={toggleTheme}
        checkedChildren="🌜"
        unCheckedChildren="🌞"
      />
    ],
    // 禁用面包屑
    breadcrumbRender: false,
    avatarProps: {
      src: initialState?.currentUser?.avatar?.trim() || undefined,
      icon: <UserOutlined />,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    // footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        const redirect = encodeURIComponent(location.pathname + location.search + location.hash);
        history.push(`${loginPath}?redirect=${redirect}`);
      }
    },


    // bgLayoutImgList: [
    //   {
    //     src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
    //     left: 85,
    //     bottom: 100,
    //     height: '303px',
    //   },
    //   {
    //     src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
    //     bottom: -68,
    //     right: -45,
    //     height: '303px',
    //   },
    //   {
    //     src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
    //     bottom: 0,
    //     left: 0,
    //     width: '331px',
    //   },
    // ],
    links: isDev
      ? [
        <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          <LinkOutlined />
          <span>OpenAPI 文档</span>
        </Link>,
      ]
      : [],
    menuHeaderRender: undefined,
    menu: {
      locale: false,
    },
    menuDataRender: (menuData) => (studioNav?.length ? withStudioMenu(menuData, studioNav) : menuData),
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <ConfigProvider theme={getDynamicThemeConfig(theme as 'light' | 'dark')}>
          {children}
        </ConfigProvider>
      );
    },
    ...initialState?.settings,
    ...proLayoutThemeConfigs[theme as 'light' | 'dark'],
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};
